/* Printable HTML document templates for the UtilityAdmin "Documents" generator.
   Rendered inside an iframe (srcDoc) so styling is isolated and Kannada Unicode
   works via Google Fonts. Both Print and Download go through the browser's
   print dialog ("Save as PDF" for download). */

export const DOC_TYPES = [
  { key: "tc", label: "School Transfer Certificate", desc: "Official Karnataka bilingual transfer certificate (25 fields).", schoolLevel: false },
  { key: "bonafide", label: "Bonafide Certificate", desc: "Bonafide proforma for Children Education Allowance.", schoolLevel: false },
  { key: "admit", label: "Mid-Term Admission Ticket", desc: "Exam admission ticket with the oral/written schedule.", schoolLevel: false },
  { key: "admit_sa", label: "Summative Assessment Admission Ticket", desc: "SA-01 admission ticket with the exam schedule.", schoolLevel: false },
  { key: "admission_form", label: "Application for Admission", desc: "School admission application form (15 fields).", schoolLevel: false },
  { key: "study_proforma", label: "Proforma for Study Certificate", desc: "BEO/DDPI counter-signed study certificate proforma.", schoolLevel: false },
  { key: "study_certificate", label: "Study Certificate", desc: "School study certificate with Trishul logo + school-name header, counter-signed by BEO/D.D.P.I.", schoolLevel: false },
  { key: "verification_kn", label: "Verification Certificate (Kannada)", desc: "ಶಾಲಾ / ಕಾಲೇಜು ದೃಢೀಕರಣ ಪತ್ರ — Kannada school verification certificate.", schoolLevel: false },
  { key: "tc_req", label: "TC Requisition Letter", desc: "Letter requesting a student's TC from the previous school.", schoolLevel: false },
  { key: "rte", label: "RTE Fee Requisition Letter", desc: "Kannada letter to the department requesting RTE fee reimbursement (school-level).", schoolLevel: true },
];

/* Reusable per-student details that documents auto-fill. Saved onto the student
   record (doc_extra) so they're typed once and reused across all documents. */
export const EXTRA_FIELDS = [
  { key: "mother_name", label: "Mother's Name" },
  { key: "admission_no", label: "Admission / Register No." },
  { key: "admission_date", label: "Date of Admission" },
  { key: "enrollment_no", label: "Enrollment / Cumulative Record No." },
  { key: "sats_no", label: "SATS / STS No." },
  { key: "religion", label: "Religion" },
  { key: "caste", label: "Caste" },
  { key: "nationality", label: "Nationality" },
  { key: "mother_tongue", label: "Mother Tongue" },
  { key: "place_of_birth", label: "Place of Birth" },
  { key: "aadhar_child", label: "Aadhar — Child" },
  { key: "aadhar_father", label: "Aadhar — Father" },
  { key: "aadhar_mother", label: "Aadhar — Mother" },
  { key: "caste_cert_no", label: "Caste Certificate No." },
  { key: "mother_mobile", label: "Mother Mobile No." },
  { key: "occupation_income", label: "Parents' Occupation & Yearly Income" },
  { key: "bpl", label: "BPL (Yes/No)" },
  { key: "bpl_card_no", label: "BPL Card No." },
  { key: "languages", label: "Languages Studied" },
  { key: "electives", label: "Electives Studied" },
  { key: "medium", label: "Medium of Instruction" },
];

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const line = (s) => (s && String(s).trim() ? `<span class="b">${esc(s)}</span>` : "<span style='display:inline-block;min-width:120px;border-bottom:1px solid #333'>&nbsp;</span>");

/* ── Field definitions per doc type (with defaults pulled from the record) ── */
export function buildForm(type, data) {
  const s = (data && data.student) || {};
  const sc = (data && data.school) || {};
  const ay = (data && data.academic_year) || "";
  const ex = s.extra || {};
  const cls = s.class_name ? `${s.class_name}${s.section ? "-" + s.section : ""}` : "";

  const F = (key, label, def = "", opts = {}) => ({ key, label, default: def, ...opts });

  switch (type) {
    case "tc":
      return [
        F("tc_no", "TC No."),
        F("school_code", "School Code", sc.school_code),
        F("academic_year", "Academic Year", ay),
        F("admission_no", "1. Admission No.", ex.admission_no),
        F("enrollment_no", "2. Cumulative Record / Enrollment No.", ex.enrollment_no),
        F("admission_date", "3. Date of Admission to School", ex.admission_date),
        F("name", "4. Name of the Student in full", s.name),
        F("sex", "5. Sex", s.sex),
        F("nationality", "6. Nationality", ex.nationality || "Indian"),
        F("religion", "7. Religion", ex.religion),
        F("caste", "7. Caste", ex.caste),
        F("father", "8. Name of the Father", s.parent_name),
        F("mother", "9. Name of the Mother", ex.mother_name),
        F("scst", "10. Whether SC / ST", ""),
        F("qualified", "11. Qualified for promotion to higher standard?", "YES"),
        F("dob_words", "12. Date of Birth (in words)", s.dob_words),
        F("district", "13. District / Local Body / Ward", "Davangere"),
        F("standard", "14. Standard studying at time of leaving", s.class_name ? `Class ${s.class_name}` : ""),
        F("languages", "15a. Languages Studied", ex.languages || "Kannada, English, Hindi"),
        F("electives", "15b. Elective Studied", ex.electives || "Mathematics, Social Science, Science"),
        F("medium", "16. Medium of Instructions", ex.medium || "English"),
        F("fees_paid", "17. Paid all fees due?", "YES"),
        F("fee_concession", "18. Fee Concession, if any", ""),
        F("scholarship", "19. Scholarship, if any", ""),
        F("medically", "20. Whether Medically Examined?", "YES"),
        F("last_attendance", "21. Month of last attendance"),
        F("app_received", "22. Date TC application received"),
        F("school_days", "23. No. of School Days up to leaving"),
        F("days_attended", "24. Total Days attended"),
        F("conduct", "25. Character and Conduct", "Satisfactory"),
        F("date_entry", "Date of Entry"),
        F("date_issue", "Date of Issue"),
      ];
    case "bonafide":
      return [
        F("date", "Date"),
        F("name", "Student Name", s.name),
        F("father", "Father / Guardian Name", s.parent_name),
        F("roll", "Roll No.", s.roll_number),
        F("std", "Standard (Std)", s.class_name),
        F("year", "Academic Year", ay),
        F("dob", "Date of Birth", s.dob),
        F("dob_words", "Date of Birth (in words)", s.dob_words),
        F("gender", "Gender (his/her)", (s.sex === "Girl" ? "her" : s.sex === "Boy" ? "his" : "his/her")),
        F("admission_reg_no", "Admission Register No.", ex.admission_no),
      ];
    case "admit":
      return [
        F("exam_title", "Exam Title", "Mid - Term Examination"),
        F("name", "Student Name", s.name),
        F("reg_no", "Reg. No.", s.student_id),
        F("std", "Std", cls),
        F("schedule", "Schedule (one per line: Date | Day | Subject | Time; use ORAL / WRITTEN as section markers)",
          "ORAL\n29/09 | Friday | Rhymes/Story Telling | 10:00am - 1:00pm\n30/09 | Saturday | Kannada/Computer | 10:00am - 1:00pm\n03/10 | Tuesday | Mathematics / Hindi | 10:00am - 1:00pm\n04/10 | Wednesday | English/G.K/Draw | 10:00am - 1:00pm\nWRITTEN\n05/10 | Thursday | Mathematics | 10:00am - 1:00pm\n06/10 | Friday | English | 10:00am - 1:00pm\n07/10 | Saturday | Kannada | 10:00am - 1:00pm\n09/10 | Monday | Hindi | 10:00am - 1:00pm",
          { type: "textarea" }),
      ];
    case "admit_sa":
      return [
        F("exam_title", "Exam Title", "SUMMATIVE ASSESSMENT - 1 (SA-01) OCT-2026"),
        F("name", "Student Name", s.name),
        F("reg_no", "Reg. No.", s.student_id),
        F("std", "Std", cls),
        F("schedule", "Schedule (one per line: Date | Day | Subject | Time)",
          "11/03/2026 | Monday | Mathematics | 9:00am - 12:00pm\n12/03/2026 | Tuesday | Social Science | 9:00am - 11:30am\n13/03/2026 | Wednesday | Kannada | 9:00am - 12:00pm\n14/03/2026 | Thursday | Science | 9:00am - 11:30am\n15/03/2026 | Friday | English | 9:00am - 11:30am\n16/03/2026 | Saturday | Hindi | 9:00am - 11:30am\n18/03/2026 | Monday | G.K/Dr/M.S/P.E | 9:00am - 11:30am",
          { type: "textarea" }),
      ];
    case "admission_form":
      return [
        F("app_no", "Application No.", ex.admission_no),
        F("date", "Date"),
        F("name", "1. Applicant's Name in full", s.name),
        F("student_class", "2. Student ID No. / Class", `${s.student_id || ""}${s.class_name ? " / " + s.class_name : ""}`),
        F("sex", "3. Sex", s.sex),
        F("place_birth", "4. Place of Birth", ex.place_of_birth),
        F("dob", "5. Date of Birth (in figures)", s.dob),
        F("father", "6. Father's / Guardian's Name", s.parent_name),
        F("mother", "7. Mother's Name in full", ex.mother_name),
        F("occupation", "8. Occupation of Parents & Yearly income", ex.occupation_income),
        F("mother_tongue", "9. Mother Tongue", ex.mother_tongue),
        F("aadhar_child", "10. Aadhar UID — Child", ex.aadhar_child),
        F("aadhar_father", "10. Aadhar UID — Father", ex.aadhar_father),
        F("aadhar_mother", "10. Aadhar UID — Mother", ex.aadhar_mother),
        F("caste_cert", "11. Student's Caste Certificate No.", ex.caste_cert_no),
        F("father_mobile", "12. Father Mobile No.", s.parent_phone),
        F("mother_mobile", "13. Mother Mobile No.", ex.mother_mobile),
        F("address", "14. Address / Residence", s.address, { type: "textarea" }),
        F("bpl", "15. Belong to BPL (Yes/No)", ex.bpl || "Yes"),
        F("bpl_no", "15. BPL Card No.", ex.bpl_card_no),
        F("last_school", "16. Last School Attended — Name", ""),
        F("last_school_addr", "16. Last School — Address", "", { type: "textarea" }),
        F("last_std_year", "17. Standard passed in Last School & year", ""),
        F("prev_medium", "18. Medium of Instruction in Previous School", ""),
        F("tc_enclosed", "19. Whether TC enclosed? (if not, reason)", ""),
        F("cert_date", "Certification — Date", ""),
        F("cert_place", "Certification — Place", "Davangere"),
      ];
    case "study_proforma":
      return [
        F("date", "Date"),
        F("admission_no", "Admission No.", ex.admission_no),
        F("sats_no", "SATS No.", ex.sats_no || s.student_id),
        F("name", "Student Name", s.name),
        F("father", "Son/Daughter of Sri", s.parent_name),
        F("gender", "Gender (he/she)", (s.sex === "Girl" ? "she" : s.sex === "Boy" ? "he" : "he/she")),
        F("from_std", "Studied from (standard)"),
        F("to_std", "to (standard)", s.class_name),
        F("from_year", "from (academic year)"),
        F("to_year", "to (academic year)", ay),
        F("caste", "Caste", ex.caste),
        F("mother_tongue", "Mother tongue", ex.mother_tongue),
      ];
    case "study_certificate":
      return [
        F("date", "Date"),
        F("name", "Student Name (Sri/Smt./Kum.)", s.name),
        F("father", "S/o. D/o.", s.parent_name),
        F("gender", "Gender (he/she)", (s.sex === "Girl" ? "she" : s.sex === "Boy" ? "he" : "he/she")),
        F("from_std", "Studied from (standard)"),
        F("to_std", "to (standard)", s.class_name),
        F("medium", "Medium of instruction", ex.medium || "English"),
        F("passed_std", "Passed (Standard)", s.class_name),
        F("from_year", "During (from academic year)"),
        F("to_year", "to (academic year)", ay),
        F("caste", "Caste", ex.caste),
        F("mother_tongue", "Mother Tongue", ex.mother_tongue),
        F("dob", "Date of Birth", s.dob),
        F("place_of_birth", "Place of Birth", ex.place_of_birth),
        F("head_name", "Head of institution — Name (in Block letters)"),
      ];
    case "verification_kn":
      return [
        F("school_name", "ಶಾಲೆ/ಕಾಲೇಜು ಹೆಸರು (School name)", sc.name),
        F("dise_code", "ಡೈಸ್ ಕೋಡ್ (DISE code)", sc.dise_code),
        F("sts_no", "ಎಸ್.ಟಿ.ಎಸ್. ಸಂಖ್ಯೆ (STS No.)", ex.sats_no || s.student_id),
        F("name", "ಕು|| (Student name)", s.name),
        F("father", "ತಂದೆ (Father)", s.parent_name),
        F("mother", "ತಾಯಿ (Mother)", ex.mother_name),
        F("taluk", "ತಾಲ್ಲೂಕು (Taluk)", "Davangere"),
        F("district", "ಜಿಲ್ಲೆ (District)", "Davangere"),
        F("dob", "ಹುಟ್ಟಿದ ದಿನಾಂಕ (DOB)", s.dob),
        F("dob_words", "ಅಕ್ಷರದಲ್ಲಿ (DOB in words)", s.dob_words),
        F("caste", "ಜಾತಿ (Caste)", ex.caste),
        F("adm_no", "ದಾಖಲಾತಿ ನಂ. (Admission No.)", ex.admission_no),
        F("adm_date", "ದಾಖಲಾತಿ ದಿನಾಂಕ (Admission date)", ex.admission_date),
        F("place", "ಸ್ಥಳ (Place)", "Davangere"),
        F("date", "ದಿನಾಂಕ (Date)"),
      ];
    case "tc_req":
      return [
        F("date", "Date"),
        F("prev_school", "Previous School (To: address lines)", "", { type: "textarea" }),
        F("name", "Student Name", s.name),
        F("father", "Father / Guardian Name", s.parent_name),
        F("prev_std", "Standard studied last year", s.class_name),
      ];
    case "rte":
      return [
        F("date", "Date"),
        F("year", "Academic Year", ay),
        F("amount1", "First installment (₹)", "3,60,000"),
        F("amount2", "Second installment (₹)", "3,60,000"),
        F("total", "Total (₹)", "7,20,000"),
      ];
    default:
      return [];
  }
}

/* ── Full-HTML wrapper (fonts + print CSS) ── */
function wrap(inner, logoUrl) {
  return `<!doctype html><html><head><meta charset="utf-8">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Kannada:wght@400;600;700&family=Tinos:wght@400;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Tinos','Noto Sans Kannada',serif; color:#111; background:#ffffff; margin:0; padding:16px; font-size:13px; line-height:1.5; }
  .kn { font-family:'Noto Sans Kannada',sans-serif; }
  .doc { max-width: 790px; margin: 0 auto; }
  .center { text-align:center; }
  .b { font-weight:700; }
  .hdr-logo { width:64px; height:64px; object-fit:contain; }
  table { border-collapse: collapse; width:100%; }
  .box { border:2px solid #111; padding:0; }
  .tcgrid td { border:1px solid #111; padding:5px 7px; vertical-align:top; font-size:11.5px; }
  .fld-val { font-weight:700; }
  .underline { display:inline-block; min-width:140px; border-bottom:1px solid #333; }
  .sig-row { display:flex; justify-content:space-between; margin-top:48px; }
  .sched th, .sched td { border:1px solid #111; padding:5px 8px; text-align:center; font-size:12px; }
  .sched .sec { background:#f3d9e4; font-weight:700; }
  @media print { body { padding:0; } .no-print { display:none !important; } }
</style></head><body><div class="doc">${inner}</div></body></html>`;
}

const logoImg = (logoUrl, size = 64) => (logoUrl ? `<img class="hdr-logo" src="${logoUrl}" style="width:${size}px;height:${size}px" />` : "");

/* ── Individual document bodies ── */
function tcBody(v, data) {
  const sc = (data && data.school) || {};
  const cell = (num, label, kn, val, raw = false) =>
    `<div style="margin-bottom:3px"><span class="b">${esc(label)}</span>${kn ? ` <span class="kn" style="color:#333">/ ${esc(kn)}</span>` : ""}</div><div class="fld-val">${raw ? val : line(val)}</div>`;
  const left = [
    cell(1, "1. Admission No.", "ಪ್ರವೇಶ ದಾಖಲಾತಿ ಸಂಖ್ಯೆ", v.admission_no),
    cell(2, "2. Cumulative Record No. / Enrollment No.", "ಸಂಚಿತ ದಾಖಲಾತಿ ಸಂಖ್ಯೆ", v.enrollment_no),
    cell(3, "3. Date of Admission to School", "ಶಾಲೆಗೆ ದಾಖಲಾದ ದಿನಾಂಕ", v.admission_date),
    cell(4, "4. Name of the Student in full", "ವಿದ್ಯಾರ್ಥಿಯ ಪೂರ್ಣ ಹೆಸರು", v.name),
    cell(5, "5. Sex", "ಲಿಂಗ", v.sex),
    cell(6, "6. Nationality", "ರಾಷ್ಟ್ರೀಯತೆ", v.nationality),
    cell(7, "7. Religion / ಧರ್ಮ · Caste / ಜಾತಿ", "", `${v.religion || ""}${v.caste ? " / " + v.caste : ""}`),
    cell(8, "8. Name of the Father", "ತಂದೆಯ ಹೆಸರು", v.father),
    cell(9, "9. Name of the Mother", "ತಾಯಿಯ ಹೆಸರು", v.mother),
    cell(10, "10. Whether the candidate belongs to Schedule Caste or Schedule Tribe?", "ವಿದ್ಯಾರ್ಥಿಯು ಪರಿಶಿಷ್ಠ ಜಾತಿ ಅಥವಾ ಪರಿಶಿಷ್ಠ ವರ್ಗಕ್ಕೆ ಸೇರಿದವನಾಗಿದ್ದಾನೆಯೇ ?", v.scst),
    cell(11, "11. Whether qualified for promotion to a higher Standard?", "ಉನ್ನತ ಶಿಕ್ಷಣಕ್ಕಾಗಿ ಬಡ್ತಿ ಪಡೆಯಲು ಅರ್ಹತೆ ಇದೆಯೇ ?", v.qualified),
    cell(12, "12. Student Date of Birth", "ವಿದ್ಯಾರ್ಥಿಯ ಹುಟ್ಟಿದ ದಿನಾಂಕ (ಅಕ್ಷರಗಳಲ್ಲಿ)", v.dob_words),
    cell(13, "13. District / Local Body / Ward", "ಜಿಲ್ಲೆ / ಸ್ಥಳೀಯ ಸಂಸ್ಥೆ / ವಾರ್ಡ್", v.district),
  ];
  const right = [
    cell(14, "14. Standard in which the student is studying at time of leaving the school", "ಶಾಲೆ ಬಿಡುವಾಗ ವಿದ್ಯಾರ್ಥಿ ಓದುತ್ತಿದ್ದ ತರಗತಿ", v.standard),
    cell(15, "15. Student opted Subjects", "ವಿದ್ಯಾರ್ಥಿ ಆಯ್ಕೆ ಮಾಡಿದ ವಿಷಯಗಳು", `<div style="font-weight:400">a) Languages Studied <span class="kn">/ ಎ) ಅಧ್ಯಯನ ಮಾಡಿದ ಭಾಷೆ</span> : <b>${v.languages ? esc(v.languages) : "<span class='underline'>&nbsp;</span>"}</b></div><div style="font-weight:400;margin-top:4px">b) Elective Studied <span class="kn">/ ಬಿ) ಅಧ್ಯಯನ ಮಾಡಿದ ಐಚ್ಛಿಕ ವಿಷಯಗಳು</span> : <b>${v.electives ? esc(v.electives) : "<span class='underline'>&nbsp;</span>"}</b></div>`, true),
    cell(16, "16. Medium of Instructions", "ಬೋಧನ ಮಾಧ್ಯಮ", v.medium),
    cell(17, "17. Whether the Student has paid all the fees due to School?", "ಶಾಲೆಗೆ ಬಾಕಿ ಇರುವ ಎಲ್ಲಾ ಶುಲ್ಕಗಳನ್ನು ವಿದ್ಯಾರ್ಥಿಯು ಪಾವತಿ ಮಾಡಿದ್ದಾನೆಯೇ", v.fees_paid),
    cell(18, "18. Fee Concession, if any (Nature and period to be specified)", "ಶುಲ್ಕ ವಿನಾಯಿತಿ ಯಾವುದಾದರೂ ಇದ್ದಲ್ಲಿ (ಸ್ವರೂಪ ಮತ್ತು ಅವಧಿಯನ್ನು ನಿರ್ಧಿಷ್ಟಪಡಿಸತಕ್ಕದ್ದು)", v.fee_concession),
    cell(19, "19. Scholarship, if any (Nature and period to be specified)", "ವಿದ್ಯಾರ್ಥಿ ವೇತನ ಯಾವುದಾದರೂ ಇದ್ದಲ್ಲಿ (ಸ್ವರೂಪ ಮತ್ತು ಅವಧಿಯನ್ನು ನಿರ್ಧಿಷ್ಟಪಡಿಸತಕ್ಕದ್ದು)", v.scholarship),
    cell(20, "20. Whether Medically Examined or not", "ವೈದ್ಯಕೀಯ ಪರೀಕ್ಷೆ ಮಾಡಲಾಗಿದೆಯೇ / ಇಲ್ಲವೇ", v.medically),
    cell(21, "21. Month of Student last attendance at the School", "ಶಾಲೆಗೆ ವಿದ್ಯಾರ್ಥಿಯು ಕಡೆಯ ಬಾರಿ ಹಾಜರಾಗಿದ್ದ ದಿನಾಂಕ / ತಿಂಗಳು", v.last_attendance),
    cell(22, "22. Date on which the application for Transfer Certificate was received", "ವರ್ಗಾವಣೆ ಪ್ರಮಾಣ ಪತ್ರಕ್ಕಾಗಿ ಅರ್ಜಿಯನ್ನು ಸ್ವೀಕರಿಸಿದ ದಿನಾಂಕ", v.app_received),
    cell(23, "23. Number of School Days up to the date of leaving in academic year", "ಶಾಲೆ ಬಿಟ್ಟ ದಿನಾಂಕದವರೆಗಿನ ದಿನಗಳ ಸಂಖ್ಯೆ", v.school_days),
    cell(24, "24. Number of total days the Student attended in academic year", "ವಿದ್ಯಾರ್ಥಿಯು ಹಾಜರಾಗಿದ್ದ ಶಾಲೆ ದಿನಗಳ ಸಂಖ್ಯೆ", v.days_attended),
    cell(25, "25. Character and Conduct", "ನಡತೆ ಮತ್ತು ಚಾರಿತ್ರ್ಯ", v.conduct),
  ];
  const col = (arr) => arr.map((c, i) => `<div style="flex:1;padding:6px 8px;border-bottom:${i < arr.length - 1 ? "1px solid #111" : "none"};font-size:11.5px">${c}</div>`).join("");
  return `
  <div class="box" style="padding:0">
    <div style="padding:8px 14px 4px">
      <table style="border:none"><tr>
        <td style="border:none;text-align:left;font-size:11px">TC No. : <span class="b">${line(v.tc_no)}</span><br/>Academic year : <span class="b">${esc(v.academic_year)}</span></td>
        <td style="border:none;text-align:right;font-size:11px;vertical-align:top">School Code : <span class="b">${esc(v.school_code)}</span></td>
      </tr></table>
      <div class="center" style="margin-top:4px">
        <div class="b" style="font-size:16px">${esc(sc.tc_header_line1)}</div>
        <div style="font-size:11px">${esc(sc.tc_header_line2)}</div>
        <div class="b kn" style="font-size:15px;margin-top:2px">${esc(sc.tc_header_line3)}</div>
        <div class="b" style="font-size:15px;margin-top:2px;letter-spacing:1px">SCHOOL TRANSFER CERTIFICATE</div>
      </div>
    </div>
    <div style="display:flex;border-top:1.5px solid #111;align-items:stretch">
      <div style="width:50%;border-right:1.5px solid #111;display:flex;flex-direction:column">${col(left)}</div>
      <div style="width:50%;display:flex;flex-direction:column">${col(right)}</div>
    </div>
    <div style="border-top:1.5px solid #111;padding:8px 14px">
      <table style="border:none"><tr>
        <td style="border:none;font-size:12px">Date of Entry : <span class="b">${line(v.date_entry)}</span></td>
        <td style="border:none;font-size:12px;text-align:right">Date of Issue : <span class="b">${line(v.date_issue)}</span></td>
      </tr></table>
      <div style="height:70px"></div>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;font-size:12px">
        <div>Data Entry operator Sign : ______________</div>
        <div class="center">School Seal</div>
      </div>
    </div>
  </div>`;
}

function schoolHeader(logoUrl, sc, title) {
  return `<table style="border:none"><tr>
    <td style="border:none;width:78px">${logoImg(logoUrl)}</td>
    <td style="border:none;text-align:center">
      <div class="b" style="font-size:22px;letter-spacing:1px">${esc(sc.name)}</div>
      <div style="font-size:12px">${esc(sc.campus)}, ${esc(sc.city)}</div>
      <div style="font-size:11px">Ph: ${esc(sc.phone)} &nbsp;&nbsp; DISE CODE: ${esc(sc.dise_code)}</div>
    </td>
    <td style="border:none;width:78px"></td>
  </tr></table>
  <div style="border-top:2px solid #111;margin:6px 0 14px"></div>
  ${title ? `<div class="center b" style="font-size:16px;margin-bottom:10px">${title}</div>` : ""}`;
}

function bonafideBody(v, data) {
  const sc = (data && data.school) || {};
  return `
  ${schoolHeader(data && data.logoUrl, sc, "BONAFIDE CERTIFICATE PROFORMS")}
  <div style="text-align:right;font-size:12px;margin-top:-8px">Date: ${line(v.date)}</div>
  <div class="center" style="margin:6px 0 16px">( For Children Education Allowance )</div>
  <p style="font-size:14px;line-height:2">
    This is to certify that <span class="b">${line(v.name)}</span> S/O, <span class="b">${line(v.father)}</span>
    Roll No – <span class="b">${line(v.roll)}</span> is a Bonafide student of this institution studying
    Std <span class="b">${line(v.std)}</span> during this Year <span class="b">${line(v.year)}</span>.
    ${(v.gender || "his") === "her" ? "Her" : "His"} date of birth is <span class="b">${line(v.dob)}</span>
    (<span class="b">${line(v.dob_words)}</span>). ${(v.gender || "his") === "her" ? "She" : "He"} bears good moral character.
  </p>
  <p style="font-size:14px;margin-top:22px">Admission Register No: <span class="b">${line(v.admission_reg_no)}</span></p>
  <p style="font-size:14px">The above details are true and correct according to our school records.</p>
  <div class="sig-row"><div class="b" style="font-size:15px">Thank You</div><div></div></div>`;
}

function admitBody(v, data) {
  const sc = (data && data.school) || {};
  const rows = String(v.schedule || "").split("\n").map((r) => r.trim()).filter(Boolean).map((r) => {
    const up = r.toUpperCase();
    if (up === "ORAL" || up === "WRITTEN") return `<tr><td class="sec" colspan="5">${esc(r.toUpperCase())}</td></tr>`;
    const c = r.split("|").map((x) => x.trim());
    return `<tr><td>${esc(c[0] || "")}</td><td>${esc(c[1] || "")}</td><td>${esc(c[2] || "")}</td><td>${esc(c[3] || "")}</td><td></td></tr>`;
  }).join("");
  return `
  <div class="center">
    <table style="border:none"><tr>
      <td style="border:none;width:70px">${logoImg(data && data.logoUrl, 56)}</td>
      <td style="border:none;text-align:center">
        <div class="b" style="font-size:20px">${esc(sc.name)}</div>
        <div style="font-size:12px">${esc(sc.campus)}, ${esc(sc.city)}</div>
        <div class="b" style="font-size:14px;margin-top:4px">${line(v.exam_title)}</div>
        <div class="b" style="font-size:15px">Admission Ticket</div>
      </td>
      <td style="border:none;width:70px"></td>
    </tr></table>
  </div>
  <table style="border:none;margin:10px 0"><tr>
    <td style="border:none;font-size:13px">Name: <span class="b">${line(v.name)}</span><br/><br/>Reg. No: <span class="b">${line(v.reg_no)}</span></td>
    <td style="border:none;font-size:13px;text-align:right;vertical-align:bottom">Std: <span class="b">${line(v.std)}</span></td>
  </tr></table>
  <table class="sched">
    <tr><th>Date</th><th>Day</th><th>Sub</th><th>Time</th><th>T. Sign</th></tr>
    ${rows}
  </table>
  <div class="sig-row"><div>Class Teacher Sign</div><div>Head Master Sign</div></div>`;
}

function tcReqBody(v, data) {
  const sc = (data && data.school) || {};
  const prev = String(v.prev_school || "").split("\n").map((l) => esc(l)).join("<br/>") || "<br/><br/>";
  const ord = (val) => {
    const s = String(val || "").trim();
    const m = s.match(/^(\d+)$/);
    if (!m) return s;
    const n = parseInt(m[1], 10);
    const suf = (n % 100 >= 11 && n % 100 <= 13) ? "th" : (["th", "st", "nd", "rd"][n % 10] || "th");
    return `${n}${suf}`;
  };
  return `
  <div class="center">
    <table style="border:none"><tr>
      <td style="border:none;width:78px">${logoImg(data && data.logoUrl)}</td>
      <td style="border:none;text-align:center"><div class="b" style="font-size:24px;color:#1e4fa3;letter-spacing:1px">TRISHUL SCHOOL</div>
      <div style="font-size:12px">${esc(sc.campus)}, ${esc(sc.city)}</div></td>
      <td style="border:none;width:78px"></td>
    </tr></table>
  </div>
  <div style="border-top:2px solid #111;margin:6px 0 14px"></div>
  <table style="border:none"><tr>
    <td style="border:none;font-size:12px">DISE CODE: <span class="b">${esc(sc.dise_code)}</span></td>
    <td style="border:none;font-size:12px;text-align:right">Date: ${line(v.date)}</td>
  </tr></table>
  <p style="font-size:13px;margin-top:10px">To,<br/>The Principal / Head Master,<br/>${prev}</p>
  <p style="font-size:13px;margin-top:10px">Respected Sir/Madam</p>
  <p style="font-size:13px"><span class="b">Sub: Requisition to send the Transfer Certificate.</span></p>
  <p style="font-size:13px;line-height:2;margin-top:8px">
    With reference to the above Subject, I hereby request you to send the Transfer Certificate of
    <span class="b">${line(v.name)}</span> S/O or D/O <span class="b">${line(v.father)}</span> had been studying in
    <span class="b">${line(ord(v.prev_std))}</span> std last year in your institution. Now he/she requested to take admission
    in our institution. Please send the T.C. of the student to the above mentioned address of the school.
    Kindly send the T.C. as soon as possible. Please mention the Student's STS number &amp; school DISE Code.
  </p>
  <p style="font-size:13px;margin-top:14px">Thanking you..</p>
  <div class="sig-row"><div>Signature of the Parent</div><div>Yours sincerely,</div></div>`;
}

function rteBody(v, data) {
  const sc = (data && data.school) || {};
  return `
  <div class="center">
    <table style="border:none"><tr>
      <td style="border:none;width:70px">${logoImg(data && data.logoUrl, 56)}</td>
      <td style="border:none;text-align:center"><div class="b" style="font-size:22px">${esc(sc.name)}</div>
      <div style="font-size:12px">${esc(sc.campus)}, ${esc(sc.city)}</div>
      <div style="font-size:11px">Ph No – ${esc(sc.phone)} &nbsp;&nbsp; DISE CODE - ${esc(sc.dise_code)}</div></td>
      <td style="border:none;width:70px"></td>
    </tr></table>
  </div>
  <div style="border-top:1.5px solid #111;margin:6px 0 14px"></div>
  <table style="border:none"><tr>
    <td class="kn" style="border:none;font-size:13px">ಗೆ,</td>
    <td class="kn" style="border:none;font-size:13px;text-align:right">ದಿನಾಂಕ: ${line(v.date)}</td>
  </tr></table>
  <div class="kn" style="font-size:13px;line-height:1.9;margin-top:6px">
    <p style="padding-left:22px;text-indent:-22px">1. ಮಾನ್ಯ ಆಯುಕ್ತರ ಕಛೇರಿ,<br/>ಶಾಲಾ ಶಿಕ್ಷಣ ಇಲಾಖೆ,<br/>ನೃಪತುಂಗ ರಸ್ತೆ, ಬೆಂಗಳೂರು.</p>
    <p style="margin-top:8px;padding-left:22px;text-indent:-22px">2. ಮಾನ್ಯ ಉಪನಿರ್ದೇಶಕರು,<br/>ಶಾಲಾ ಶಿಕ್ಷಣ ಇಲಾಖೆ,<br/>ದಾವಣಗೆರೆ.</p>
    <p style="margin-top:8px;padding-left:22px;text-indent:-22px">3. ಮಾನ್ಯ ಕ್ಷೇತ್ರ ಶಿಕ್ಷಣಾಧಿಕಾರಿಗಳು,<br/>ದಕ್ಷಿಣ ವಲಯ,<br/>ದಾವಣಗೆರೆ.</p>
    <p style="margin-top:10px">ಮಾನ್ಯರೇ,</p>
    <p class="b" style="margin-top:6px">ವಿಷಯ : ಆರ್.ಟಿ.ಇ. ಶುಲ್ಕ ಪಾವತಿಸುವ ಬಗ್ಗೆ.</p>
    <p style="margin-top:8px">ಈ ಮೇಲ್ಕಂಡ ವಿಷಯಕ್ಕೆ ಸಂಬಂಧಿಸಿದಂತೆ <span class="b">${esc(v.year)}</span> ನೇ ಸಾಲಿನ ಮೊದಲನೆಯ ಕಂತು
    <span class="b">${esc(v.amount1)}</span> ರೂ, ಎರಡನೆಯ ಕಂತು <span class="b">${esc(v.amount2)}</span> ರೂ, ಒಟ್ಟು
    <span class="b">${esc(v.total)}</span> ರೂ ಆರ್.ಟಿ.ಇ. ಶುಲ್ಕ ನಮಗೆ ಆರ್ಥಿಕ ತೊಂದರೆ ಇರುವುದರಿಂದ ಆರ್.ಟಿ.ಇ. ಮೊತ್ತವನ್ನು
    ಪಾವತಿಸಿ ಕೊಡಬೇಕಾಗಿ ತಮ್ಮಲ್ಲಿ ವಿನಯಪೂರ್ವಕವಾಗಿ ವಿನಂತಿ. ತಮ್ಮ ಮುಂದಿನ ಆದೇಶಕ್ಕಾಗಿ,</p>
    <p class="center" style="margin-top:24px">ವಂದನೆಗಳೊಂದಿಗೆ</p>
    <p style="text-align:right">ಇಂತಿ, ತಮ್ಮ ವಿಶ್ವಾಸಿ</p>
  </div>`;
}

function admissionFormBody(v, data) {
  const sc = (data && data.school) || {};
  const row = (label, val, extra = "") =>
    `<tr><td style="width:44%;font-size:11.5px">${esc(label)}</td><td style="font-size:12px" class="b">${line(val)}${extra}</td></tr>`;
  return `
  <div class="box" style="padding:12px 16px">
    <div class="center" style="font-size:12px">APPLICATION FOR ADMISSION</div>
    <table style="border:none"><tr>
      <td style="border:none;width:100px">${logoImg(data && data.logoUrl, 92)}</td>
      <td style="border:none;text-align:center">
        <div class="b" style="font-size:20px">TRISHUL SCHOOL, DAVANGERE - 577002.</div>
        <div style="font-size:11px">ENGLISH MEDIUM</div>
        <div style="font-size:11px">(NURSERY, PRIMARY &amp; HIGH SCHOOL)</div>
        <div style="font-size:11px">(RECOGNIZED BY GOVERNMENT OF KARNATAKA)</div>
      </td>
      <td style="border:none;width:70px"><div style="border:1px solid #111;width:60px;height:70px;font-size:9px;text-align:center;padding-top:26px;color:#888">Photo</div></td>
    </tr></table>
    <div class="center b" style="font-size:11px;margin:4px 0 8px">READ THE INSTRUCTION BEFORE FILLING IN THE FORM</div>
    <table style="border:none"><tr>
      <td style="border:none;font-size:12px">Application No. <span class="b">${line(v.app_no)}</span></td>
      <td style="border:none;font-size:12px;text-align:right">Date: <span class="b">${line(v.date)}</span></td>
    </tr></table>
    <table class="tcgrid" style="margin-top:6px">
      ${row("1. Applicant's Name in full", v.name)}
      ${row("2. Student ID No. / Class", v.student_class)}
      ${row("3. Sex", v.sex)}
      ${row("4. Place of Birth", v.place_birth)}
      ${row("5. Date of Birth (in figures)", v.dob)}
      ${row("6. Father's / Guardian's Name in Full", v.father)}
      ${row("7. Mother Name in full", v.mother)}
      ${row("8. Occupation of Parents and Yearly income", v.occupation)}
      ${row("9. Mother Tongue", v.mother_tongue)}
      <tr><td style="font-size:11.5px">10. Aadhar UID No.</td><td style="font-size:12px">
        <div>Child: <span class="b">${line(v.aadhar_child)}</span></div>
        <div>Father: <span class="b">${line(v.aadhar_father)}</span></div>
        <div>Mother: <span class="b">${line(v.aadhar_mother)}</span></div></td></tr>
      ${row("11. Student's Caste Certificate No.", v.caste_cert)}
      ${row("12. Father Mobile No.", v.father_mobile)}
      ${row("13. Mother Mobile No.", v.mother_mobile)}
      ${row("14. Address / Residence", (v.address || "").replace(/\n/g, "<br/>"))}
      <tr><td style="font-size:11.5px">15. Belong to BPL</td><td style="font-size:12px" class="b">${line(v.bpl)} &nbsp;&nbsp; BPL Card No: ${line(v.bpl_no)}</td></tr>
      <tr><td style="font-size:11.5px">16. Last School Attended by the Student
        <div style="margin-top:4px">Name</div><div style="margin-top:14px">Address</div></td>
        <td style="font-size:12px" class="b">${line(v.last_school)}<div style="margin-top:6px">${(v.last_school_addr || "").trim() ? esc(v.last_school_addr).replace(/\n/g, "<br/>") : "<span class='underline'>&nbsp;</span>"}</div></td></tr>
      ${row("17. Standard successfully passed in the Last School and in which year", v.last_std_year)}
      ${row("18. Medium of Instruction in the Previous School", v.prev_medium)}
      ${row("19. Whether Transfer Certificate enclosed? (if not give reason)", v.tc_enclosed)}
    </table>
    <p style="font-size:12.5px;margin-top:12px">Certified that the information given above is to the best of my knowledge and I agree to oblige by all the rules and regulation of school.</p>
    <table style="border:none;margin-top:8px"><tr>
      <td style="border:none;font-size:12.5px;vertical-align:bottom">Date : <span class="b">${line(v.cert_date)}</span><div style="margin-top:10px">Place : <span class="b">${line(v.cert_place)}</span></div></td>
      <td style="border:none;font-size:12.5px;text-align:right;vertical-align:bottom">&nbsp;<div style="margin-top:26px" class="b">Signature of Parent / Guardian</div></td>
    </tr></table>
    <div class="center b" style="font-size:13px;margin:14px 0 6px;text-decoration:underline">INSTRUCTION</div>
    <ol style="font-size:11.5px;line-height:1.7;margin:0;padding-left:18px">
      <li>The Application form should be filled out in BLOCK LETTERS.</li>
      <li>All Students joining the school for the first time must produce a Certificate in support of the age from Municipal Corporation or Grama Panchayat or Town Municipal Council. Failing which the Student will not be admitted.</li>
      <li>Admission should meet the requirements of the management of this school; their conditions may change from time to time.</li>
      <li>Only children who have completed three years and ten months on June 1st will be admitted to the kindergarten class.</li>
    </ol>
    <div style="border:1.5px solid #111;border-radius:14px;margin-top:14px;padding:10px 16px">
      <div class="center b" style="font-size:13px;text-decoration:underline;margin-bottom:8px">FOR OFFICE USE ONLY</div>
      <table style="border:none;font-size:12.5px">
        <tr><td style="border:none;padding:6px 0">C.R. No. <span class="underline" style="min-width:220px">&nbsp;</span></td><td style="border:none;text-align:right;padding:6px 0">Date <span class="underline" style="min-width:150px">&nbsp;</span></td></tr>
        <tr><td style="border:none;padding:6px 0" colspan="2">Date of Admission : <span class="underline" style="min-width:320px">&nbsp;</span></td></tr>
        <tr><td style="border:none;padding:6px 0" colspan="2">Admission No. <span class="underline" style="min-width:340px">&nbsp;</span></td></tr>
        <tr><td style="border:none;padding:6px 0" colspan="2">Admitted to Standard <span class="underline" style="min-width:300px">&nbsp;</span></td></tr>
      </table>
      <div style="display:flex;justify-content:space-between;margin-top:28px;font-size:12.5px" class="b">
        <div>Head Master</div><div>Administrator</div>
      </div>
    </div>
  </div>`;
}

function studyProformaBody(v, data) {
  return `
  <div class="box" style="padding:18px 22px;min-height:640px;position:relative">
    <div class="center b" style="font-size:15px;text-decoration:underline;margin-bottom:10px">PROFORMA FOR STUDY CERTIFICATE</div>
    <div style="position:absolute;top:14px;right:20px;border:1px solid #111;width:90px;height:100px"></div>
    <div style="text-align:right;font-size:12.5px;line-height:2.2;margin-top:120px">
      <div>DATE : ${v.date ? `<span class="b">${esc(v.date)}</span>` : `<span class="underline">&nbsp;</span>`}</div>
      <div>Admission No : ${v.admission_no ? `<span class="b">${esc(v.admission_no)}</span>` : `<span class="underline">&nbsp;</span>`}</div>
      <div>SATS NO : ${v.sats_no ? `<span class="b">${esc(v.sats_no)}</span>` : `<span class="underline">&nbsp;</span>`}</div>
    </div>
    <p style="font-size:13.5px;line-height:2.4;margin-top:20px;text-align:justify">
      This is to certify that <span class="b">${line(v.name)}</span> Son/Daughter of Sri <span class="b">${line(v.father)}</span>
      has studied from <span class="b">${line(v.from_std)}</span> standard to <span class="b">${line(v.to_std)}</span> standard in our institution
      from <span class="b">${line(v.from_year)}</span> to <span class="b">${line(v.to_year)}</span> academic years.
      ${(v.gender || "he/she") === "she" ? "She" : (v.gender === "he" ? "He" : "He / She")} belongs to <span class="b">${line(v.caste)}</span> Caste
      and mother tongue of the candidate is <span class="b">${line(v.mother_tongue)}</span> as per admission Register of the institution.
    </p>
    <p style="text-align:center;margin-top:10px;font-size:13.5px">The above details are true and correct to the best of my knowledge.</p>
    <div style="text-align:right;margin-top:36px;font-size:13px">Signature of<br/>Head of the institution</div>
    <div style="border-top:1px solid #111;margin:22px 0 10px"></div>
    <div class="center" style="font-size:13px">COUNTER SIGNED BY ME BEO/DDPI</div>
    <div style="height:60px"></div>
    <div class="center" style="font-size:12.5px;margin-top:24px">Address, Seal &amp; Office Telephone Number<br/>Of the Block Education Officer / D.D.P.I.</div>
  </div>`;
}

function studyCertificateBody(v, data) {
  const sc = (data && data.school) || {};
  const g = v.gender || "he/she";
  const HeShe = g === "she" ? "She" : g === "he" ? "He" : "He / She";
  const schoolName = sc.name || "TRISHUL HIGH SCHOOL";
  const subLine = [sc.campus, sc.city].filter(Boolean).map(esc).join(", ");
  return `
  <div class="box" style="padding:20px 24px;min-height:700px;position:relative">
    <div style="text-align:right;font-size:13.5px;margin-bottom:8px">Date : ${line(v.date)}</div>
    <div class="box" style="display:flex;align-items:center;padding:12px 16px;min-height:96px">
      <div style="width:84px;flex:none;text-align:left">${logoImg(data && data.logoUrl, 74)}</div>
      <div style="flex:1;text-align:center">
        <div class="b" style="font-size:25px;letter-spacing:1px">${esc(schoolName)}</div>
        ${subLine ? `<div style="font-size:12.5px;margin-top:2px">${subLine}</div>` : ""}
      </div>
      <div style="width:84px;flex:none"></div>
    </div>
    <div class="center b" style="font-size:22px;text-decoration:underline;letter-spacing:1px;margin:20px 0 22px">STUDY CERTIFICATE</div>
    <p style="font-size:14px;line-height:2.7;text-align:justify">
      This is to Certify that Sri/Smt./Kum. <span class="b">${line(v.name)}</span>
      S/o. D/o. <span class="b">${line(v.father)}</span>
      has studied from <span class="b">${line(v.from_std)}</span> standard to <span class="b">${line(v.to_std)}</span> standard
      in our institution in <span class="b">${line(v.medium)}</span> Medium and passed <span class="b">${line(v.passed_std)}</span> Standard
      during <span class="b">${line(v.from_year)}</span> to <span class="b">${line(v.to_year)}</span> academic years.
      ${HeShe} belongs to <span class="b">${line(v.caste)}</span> Caste
      and the Mother Tongue of the candidate is <span class="b">${line(v.mother_tongue)}</span>
      and Date of Birth is <span class="b">${line(v.dob)}</span>
      and Place of Birth is <span class="b">${line(v.place_of_birth)}</span>
      as per the Admission Register of the institution.
    </p>
    <p style="text-align:center;margin-top:16px;font-size:14px">The above details are true and correct to the best of my knowledge.</p>
    <div style="text-align:right;margin-top:44px;font-size:13.5px">Signature of<br/>Head of the institution</div>
    <div style="margin-top:22px;font-size:13.5px">(Name in Block letters ${line(v.head_name)})</div>
    <div style="border-top:1px dashed #111;margin:28px 0 8px"></div>
    <div class="center b" style="font-size:15px;letter-spacing:1px">COUNTER SIGNED BY ME</div>
    <div class="center" style="font-size:12px;margin-top:30px">Address, Seal &amp; Office Telephone Number of the Block Education Officer / D.D.P.I.</div>
  </div>`;
}

function verificationKnBody(v) {
  const dl = (val) => (val && String(val).trim())
    ? `<span class="b" style="margin-left:4px">${esc(val)}</span><span style="flex:1"></span>`
    : `<span style="border-bottom:1px dotted #333;flex:1;margin-left:4px"></span>`;
  const line2 = (label, val) => `<div style="display:flex;align-items:flex-end;margin-bottom:12px"><span>${label}</span>&nbsp;${dl(val)}</div>`;
  return `
  <div class="box" style="padding:20px 26px;min-height:640px;font-size:14px;line-height:2">
    <div class="center kn" style="border:2px solid #111;display:inline-block;padding:6px 26px;margin:0 auto 18px;font-size:18px;font-weight:700;width:100%;box-sizing:border-box">ಶಾಲಾ / ಕಾಲೇಜು ದೃಢೀಕರಣ ಪತ್ರ</div>
    ${line2("ಶಾಲೆ/ಕಾಲೇಜು ಹೆಸರು", v.school_name)}
    ${line2("ಶಾಲಾ/ಕಾಲೇಜು ಡೈಸ್ ಕೋಡ್ :", v.dise_code)}
    ${line2("ವಿದ್ಯಾರ್ಥಿಯ ಎಸ್.ಟಿ.ಎಸ್. ಸಂಖ್ಯೆ", v.sts_no)}
    ${line2("ಕು||", v.name)}
    <div style="display:flex;align-items:flex-end;margin-bottom:12px"><span>ತಂದೆ</span>&nbsp;${dl(v.father)}&nbsp;<span>ತಾಯಿ</span>&nbsp;${dl(v.mother)}</div>
    <div style="display:flex;align-items:flex-end;margin-bottom:12px"><span>ತಾಲ್ಲೂಕು</span>&nbsp;${dl(v.taluk)}&nbsp;<span>ಜಿಲ್ಲೆ</span>&nbsp;${dl(v.district)}</div>
    <p style="margin:10px 0 6px">ಇವರು ಈ ಶಾಲೆ/ಕಾಲೇಜು ವ್ಯಾಸಂಗ ಮಾಡುತ್ತಿದ್ದು/ಮಾಡಿದ್ದು ಶಾಲಾ/ಕಾಲೇಜು ದಾಖಲಾತಿಯಲ್ಲಿ ಇವರ ಹುಟ್ಟಿದ ದಿನಾಂಕ
      <span class="b">${esc(v.dob || "")}</span> (ಅಕ್ಷರದಲ್ಲಿ <span class="b">${esc(v.dob_words || "")}</span>) ಮತ್ತು ಜಾತಿ
      ${v.caste ? `<span class="b">${esc(v.caste)}</span>` : `<span style="border-bottom:1px dotted #333;display:inline-block;min-width:100px">&nbsp;</span>`} ಎಂದು ನಮೂದಾಗಿರುತ್ತದೆ.</p>
    <div style="display:flex;align-items:flex-end;margin-bottom:12px"><span>ದಾಖಲಾತಿ ನಂ.</span>&nbsp;${dl(v.adm_no)}&nbsp;<span>ದಿನಾಂಕ</span>&nbsp;${dl(v.adm_date)}&nbsp;<span>ಆಗಿದೆ ಎಂದು ದೃಢೀಕರಿಸುತ್ತೇನೆ.</span></div>
    <div style="display:flex;justify-content:space-between;margin-top:30px">
      <div>
        <div>ಸ್ಥಳ : <span class="b">${esc(v.place || "")}</span></div>
        <div style="margin-top:8px">ದಿನಾಂಕ : <span class="b">${esc(v.date || "")}</span></div>
      </div>
      <div style="text-align:right;align-self:flex-end">ಪ್ರಿನ್ಸಿಪಾಲ್ / ಮುಖ್ಯೋಪಾಧ್ಯಾಯರ ಸಹಿ</div>
    </div>
  </div>`;
}

export function renderDoc(type, values, logoUrl, data) {
  const d = { ...(data || {}), logoUrl };
  let inner = "";
  if (type === "tc") inner = tcBody(values, d);
  else if (type === "bonafide") inner = bonafideBody(values, d);
  else if (type === "admit" || type === "admit_sa") inner = admitBody(values, d);
  else if (type === "admission_form") inner = admissionFormBody(values, d);
  else if (type === "study_proforma") inner = studyProformaBody(values, d);
  else if (type === "study_certificate") inner = studyCertificateBody(values, d);
  else if (type === "verification_kn") inner = verificationKnBody(values, d);
  else if (type === "tc_req") inner = tcReqBody(values, d);
  else if (type === "rte") inner = rteBody(values, d);
  return wrap(inner, logoUrl);
}
